import React, { useCallback, useRef, useState } from 'react';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { Button as CustomButton } from '@spark/ui-components/components/Button';
import {
  atualizarJanelaDescarga,
  criarJanelaDescarga,
  excluirJanelaDescarga,
  getJanelaDescarga
} from '@spark/services/descarga';
import { notification } from '@spark/ui-components/utils/notification';
import { formatarCNPJ } from '@spark/utils/formatters';
import { useAuth } from '@/context';
import { COGNITO_GROUP_DESCARGA_JANELA } from '@/utils/cognitoGroups';
import { mascararRaiz } from '../utils/janelaDescarga';
import { useJanelasListagem } from '../hooks/useJanelasListagem';
import JanelaFormModal from './JanelaFormModal';
import JanelaHistoricoModal from './JanelaHistoricoModal';
import JanelasFilter from './JanelasFilter';
import JanelasHeader from './JanelasHeader';
import JanelasTable from './JanelasTable';

const textoConfirmacaoExclusao = (janela) => {
  const raiz = mascararRaiz(janela.cnpjRaiz);

  if (janela.tipo === 'CNPJ') {
    return `Excluir a janela "${janela.nome}"? O CNPJ ${formatarCNPJ(janela.cnpj)} passa a seguir a janela da raiz ${raiz}, se houver.`;
  }

  return `Excluir a janela "${janela.nome}"? Os CNPJs da raiz ${raiz} sem janela própria ficam sem janela de descarga.`;
};

const JanelasCard = () => {
  const { hasCognitoGroup } = useAuth();
  const podeEditar = hasCognitoGroup(COGNITO_GROUP_DESCARGA_JANELA);

  const [modal, setModal] = useState(null);
  const [loadingAcao, setLoadingAcao] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroApi, setErroApi] = useState(null);
  // Invalida o carregamento da janela a editar quando outro modal é aberto ou outra edição começa
  const edicaoRequestRef = useRef(0);

  const {
    data,
    loading,
    totalRegistros,
    filtros,
    paginationState,
    handleFilterApply,
    handlePaginationChange,
    fetchData
  } = useJanelasListagem();
  const { pageIndex } = paginationState;
  const ultimaDaPagina = data.length === 1;

  const abrirForm = useCallback((janela) => {
    setErroApi(null);
    setModal({ tipo: 'form', janela });
  }, []);

  const handleNova = useCallback(() => {
    edicaoRequestRef.current += 1;
    abrirForm(undefined);
  }, [abrirForm]);

  // A listagem não traz tudo que o modal precisa (e pode estar defasada): busca a janela antes de abrir
  const handleEditar = useCallback(async (janela) => {
    edicaoRequestRef.current += 1;
    const requestId = edicaoRequestRef.current;
    const atual = () => requestId === edicaoRequestRef.current;

    try {
      setLoadingAcao({ id: janela.id, acao: 'editar', requestId });
      const completa = await getJanelaDescarga(janela.id);
      if (atual()) abrirForm(completa);
    } catch (error) {
      console.error('Erro ao carregar janela de descarga:', error);
      if (atual()) notification.error('Não foi possível carregar a janela.');
    } finally {
      setLoadingAcao((acao) => (acao?.requestId === requestId ? null : acao));
    }
  }, [abrirForm]);

  const handleHistorico = useCallback((janela) => {
    edicaoRequestRef.current += 1;
    setModal({ tipo: 'historico', janela });
  }, []);
  const handleHistoricoGeral = useCallback(() => {
    edicaoRequestRef.current += 1;
    setModal({ tipo: 'historico', janela: null });
  }, []);
  const handleFecharModal = useCallback(() => setModal(null), []);

  const handleSalvar = useCallback(async (payload) => {
    const id = modal?.janela?.id;

    try {
      setSalvando(true);
      setErroApi(null);
      if (id) {
        await atualizarJanelaDescarga(id, payload);
      } else {
        await criarJanelaDescarga(payload);
      }
      setModal(null);
      notification.success('Janela salva.');
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar janela de descarga:', error);
      setErroApi(error?.response?.data?.error ?? 'Não foi possível salvar a janela.');
    } finally {
      setSalvando(false);
    }
  }, [modal, fetchData]);

  const handleExcluir = useCallback(async (janela) => {
    if (!window.confirm(textoConfirmacaoExclusao(janela))) return;

    try {
      setLoadingAcao({ id: janela.id, acao: 'excluir' });
      await excluirJanelaDescarga(janela.id);
      notification.success('Janela excluída.');
      // Excluiu a única linha de uma página além da primeira: volta uma página (a mudança já recarrega)
      if (ultimaDaPagina && pageIndex > 0) {
        handlePaginationChange(pageIndex - 1);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Erro ao excluir janela de descarga:', error);
      notification.error(error?.response?.data?.error || 'Não foi possível excluir a janela.');
    } finally {
      setLoadingAcao(null);
    }
  }, [fetchData, handlePaginationChange, pageIndex, ultimaDaPagina]);

  return (
    <Card>
      <CardBody>
        <JanelasHeader
          totalRegistros={totalRegistros}
          onMostrarHistoricoGeral={handleHistoricoGeral}
          actions={podeEditar ? (
            <CustomButton variant="primary" size="sm" onClick={handleNova}>
              Nova janela
            </CustomButton>
          ) : null}
        />

        <JanelasFilter filtros={filtros} loading={loading} onApply={handleFilterApply}>
          <JanelasTable
            data={data}
            loading={loading}
            totalRegistros={totalRegistros}
            paginationState={paginationState}
            onPaginationChange={handlePaginationChange}
            podeEditar={podeEditar}
            onEditar={handleEditar}
            onHistorico={handleHistorico}
            onExcluir={handleExcluir}
            loadingAcao={loadingAcao}
          />
        </JanelasFilter>

        {modal?.tipo === 'form' ? (
          <JanelaFormModal
            show
            mode={modal.janela ? 'edit' : 'create'}
            janela={modal.janela}
            loading={salvando}
            erroApi={erroApi}
            onHide={handleFecharModal}
            onSubmit={handleSalvar}
          />
        ) : null}

        {modal?.tipo === 'historico' ? (
          <JanelaHistoricoModal show janela={modal.janela} onHide={handleFecharModal} />
        ) : null}
      </CardBody>
    </Card>
  );
};

export default JanelasCard;
